// @ts-check
import { createCrudProp } from '../utils/crud-prop'
import { createSelector } from '../utils/selector-utils'
import { sortByTimeIfExists } from '../utils/sort-by'
import { optionSelectVitalsCheckpoints } from './checkpoints'
import { eventSelectors, optionSelectCaseStartOrAppTimeDay } from './events'
import { optionSelectIndividualVitalTypes } from './vital-type'

const mvProps = createCrudProp({ rootKey: 'mv', sortFn: sortByTimeIfExists })

/**
 * @param {ManualMeasurement} initial - The initial properties to apply.
 * @returns {any} - The changes to apply to the case.
 */
export const actionAddManualVital = mvProps.actionAdd

/**
 * @param {string} id - The id of the item to remove.
 * @returns {any} - The changes to apply to the case.
 */
export const actionRemoveManualVital = mvProps.actionRemove

/**
 * @param {string} id - The id of the item
 * @param {Partial<ManualMeasurement>} updates - The updates to apply.
 * @returns {any} - The changes to apply to the case.
 */
export const actionUpdateManualVital = mvProps.actionUpdate

/** @type {CaseSelector<ManualMeasurementWithId[]>} */
export const selectManualVitals = mvProps.select

/**
 * This adds manual vitals as if they were "extra" checkpoints
 *
 * @param {CaseData} caseData
 * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
 * @returns {Checkpoint[]}
 */
const optionSelectAllManualVitalsCheckpoints = (caseData, { tempUnit }) => {
  const manualVitals = selectManualVitals(caseData)
  const vitalTypes = optionSelectIndividualVitalTypes(caseData, { tempUnit })
  /** @type {Checkpoint[]} */
  const toReturn = []
  // Add manualVitals as if they are "extra" points
  for (const mv of manualVitals) {
    /** @type {DecoratedIndividualVitalType[]} */
    const vitalsInUse = []

    /** @type {{ [id: string]: CheckpointValueVital }} */
    const values = {}

    for (const vitalType of vitalTypes) {
      const { id } = vitalType
      const value = mv.vitals[id]

      if (value) {
        vitalsInUse.push(vitalType)
        values[id] = {
          value,
          valueSource: 'manual',
        }
      }
    }

    if (vitalsInUse.length > 0) {
      toReturn.push({
        id: mv.id,
        extra: true,
        time: mv.time,
        vitalsInUse,
        values,
        hasAllRelevantValues: true,
        hasAnySelectedVitals: true,
        isComplete: true,
        isBlank: false,
        selected: false,
        isFirst: false,
        isLast: false,
      })
    }
  }
  return toReturn
}

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration' | 'activeCheckpointId'
 * >} opts
 * @returns {Checkpoint[]}
 */
export const optionSelectCombinedAutoAndManualCheckpoints = (
  caseData,
  { appTime, tempUnit, maxDuration, activeCheckpointId }
) =>
  [
    ...optionSelectVitalsCheckpoints(caseData, {
      appTime,
      tempUnit,
      maxDuration,
      activeCheckpointId,
    })
      // for this, we don't want to include the very first "filler" checkpoint
      // that we create for the case that doesn't have any vitals or a time.
      .filter(item => !!item.time),
    ...optionSelectAllManualVitalsCheckpoints(caseData, {
      tempUnit,
    }),
  ].sort((a, b) => a.time - b.time)

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration' | 'activeCheckpointId'
 * >} opts
 * @returns {Checkpoint[]}
 */
export const optionSelectCombinedAutoAndManualCheckpointsWithinCaseTime = (
  caseData,
  { appTime, tempUnit, maxDuration, activeCheckpointId }
) => {
  const caseStart = eventSelectors.selectCaseStartTime(caseData)
  if (!caseStart) {
    return []
  }
  return (
    optionSelectCombinedAutoAndManualCheckpoints(caseData, {
      appTime,
      tempUnit,
      maxDuration,
      activeCheckpointId,
    })
      // NB: we do not trim off any end values here because conceivably
      // someone may take a final post-recovery vital that isn't within the
      // case time. But in that scenario, we would still want to show it.
      .filter(({ time }) => time >= caseStart)
  )
}

/**
 * @param {number} time
 * @returns {string}
 */
const toDateString = time => new Date(time).toDateString()

/**
 * @param {CaseData} caseData
 * @param {Pick<ReportRenderingInputs, 'tempUnit' | 'appTimeDay'>} opts
 * @returns {Checkpoint[]}
 */
export const optionSelectManualVitalsCheckpointsOnCaseDay = (
  caseData,
  { tempUnit, appTimeDay }
) => {
  const caseDayString = toDateString(
    optionSelectCaseStartOrAppTimeDay(caseData, appTimeDay)
  )
  return optionSelectAllManualVitalsCheckpoints(caseData, { tempUnit }).filter(
    ({ time }) => toDateString(time) === caseDayString
  )
}

const manualVitalToEventNote = vitals => {
  const strParts = []
  if (vitals.sbp && vitals.dbp) {
    strParts.push(`BP: ${vitals.sbp}/${vitals.dbp}`)
  } else if (vitals.sbp) {
    strParts.push(`SBP: ${vitals.sbp}`)
  } else if (vitals.dbp) {
    strParts.push(`DBP: ${vitals.dbp}`)
  }
  if (vitals.hr) {
    strParts.push(`HR: ${vitals.hr}`)
  }
  if (vitals.spo2) {
    strParts.push(`SpO₂: ${vitals.spo2}`)
  }
  if (vitals.rr) {
    strParts.push(`RR: ${vitals.rr}`)
  }
  if (vitals.etco2) {
    strParts.push(`EtCO₂: ${vitals.etco2}`)
  }
  return strParts.join(' ')
}

/** @type {CaseSelector<ManualVitalsEvent[]>} */
export const selectManualVitalsEvents = createSelector(
  selectManualVitals,
  manualVitals =>
    manualVitals.map(mv => ({
      id: mv.id,
      time: mv.time,
      note: `Manually Recorded Vital: ${manualVitalToEventNote(mv.vitals)}`,
      type: 'manualVital',
      static: true,
      canSet: false,
      showInEventReport: true,
      derived: true,
    }))
)
